import Cookies from 'js-cookie';

import trackedNavigate from './trackedNavigate';
import { getSession, handleLogin } from './useSession';
import { getDefaultAccount } from './useRouteData';
import RoutingUtil from '../util/RoutingUtil';

export const LoginUserFields = `
  id
  email
  name
  avatarUrl
  avatarColor
  isSuperUser
  memberships {
    id
    account {
      id
      name
      customDomain {
        id
        name
      }
    }
  }
  agencyMembership {
    id
    agency {
      id
      name
    }
  }
  settings {
    key
    name
    value
  }
  errors {
    attribute
    code
    sentence
    data {
      key
      value
      valueArray
    }
  }
`;

export const LoginFields = `
  accessToken
  refreshToken
  user {
    ${LoginUserFields}
  }
`;

const deconstructUser = (user) => {
  const userData = {
    userId: user?.id,
    userName: user?.name,
    userEmail: user?.email,
    avatarUrl: user?.avatarUrl,
    avatarColor: user?.avatarColor,
    userSettings: {},
    isSuperUser: user?.isSuperUser
  }

  if (user?.settings) {
    user.settings.forEach(s => userData.userSettings[s.key] = s.value);
  }

  return userData;
}

/**
 * Extracts accounts from memberships and returns the default account.
 * @param {Array} memberships - Array of membership objects with account property
 * @returns {Object|null} - The default account or null
 */
const getDefaultAccountFromMemberships = (memberships = []) => {
  if (!memberships.length) return null;
  const accounts = memberships.map(m => m.account).filter(Boolean);
  return getDefaultAccount(accounts);
};

// Helper function to determine prefixed path for account or agency
const getPrefixedPathForLoginRedirect = ({ id, getPathFn, fallback, desiredLocation }) => {
  if (!id) return null;
  const path = (desiredLocation && desiredLocation !== "/") ? desiredLocation : fallback;
  return getPathFn(path, id);
};

/**
 * Determines the redirect path after login based on user memberships.
 * Priority:
 * 1. Desired location from cookie (prefixed with account/agency if needed)
 * 2. Agency route (if user has agency membership)
 * 3. Account route (using default account from memberships)
 * 4. Root path
 */
const getLoginRedirectPath = (user) => {
  const desiredLocationCookie = Cookies.get("login-return-to");
  const desiredLocation = desiredLocationCookie || "/";

  if (desiredLocationCookie) {
    Cookies.remove("login-return-to");
  }

  // Super users should always be redirected to the root path so they can select an account
  if (user.isSuperUser && desiredLocation === "/") {
    return "/";
  }

  const accountRedirect = getPrefixedPathForLoginRedirect({
    id: getDefaultAccountFromMemberships(user?.memberships)?.id,
    getPathFn: RoutingUtil.getAccountPrefixedPath,
    fallback: '/dashboards',
    desiredLocation
  });

  if (accountRedirect) return accountRedirect;

  const agencyRedirect = getPrefixedPathForLoginRedirect({
    id: user?.agencyMembership?.agency?.id,
    getPathFn: RoutingUtil.getAgencyPrefixedPath,
    fallback: '',
    desiredLocation
  });

  if (agencyRedirect) return agencyRedirect;

  return "/";
};

export function login({ accessToken, refreshToken, rememberMe, user, handshakeLogoutUrl }) {
  handleLogin({
    accessToken,
    refreshToken,
    rememberMe,
    handshakeLogoutUrl,
    ...deconstructUser(user),
  });

  const redirectPath = getLoginRedirectPath(user);
  console.debug('Login redirect path:', redirectPath);

  // Use setTimeout to ensure session is saved before navigation
  // This prevents race conditions where navigation happens before React re-renders
  setTimeout(() => trackedNavigate(redirectPath), 0);
}

export function refreshUser(user) {
  const { accessToken, refreshToken, rememberMe, handshakeLogoutUrl } = getSession();

  handleLogin({
    accessToken,
    refreshToken,
    rememberMe,
    handshakeLogoutUrl,
    ...deconstructUser(user),
  });
}

export function rememberDesiredLocationAfterLogin(location) {
  console.debug(`Remembering desired location: ${location}`);
  Cookies.set("login-return-to", location, { sameSite: "Strict" });
}
